/**
 * computeRelevance — single source of truth for "how relevant is this
 * source citation to the answer, on a 0..1 scale".
 *
 * Different specialists in the BE pipeline populate different score
 * fields on every ``AndoSource``:
 *
 *   - ``web_search`` sets ``relevance_score`` directly (Tavily score,
 *     already 0..1, higher = better).
 *   - ``documents`` / ``email`` / ``chat`` ship Jina v3 reranker output
 *     as ``rerank_score`` (logits 0..1, higher = better) AND/OR the
 *     unified hybrid metric ``final_score`` (0..1, LOWER = better,
 *     ``final_score = 1 - rerank_score`` when both exist — see
 *     ``orchestration_patterns.py`` policy doc).
 *
 * The FE used to read only ``relevance_score`` which is 0 for every
 * internal source, so the chip tooltip rendered "0% RILEVANZA" for the
 * Octopus bill + Drive contract sources that were actually highly
 * relevant. The mapper here picks the most informative field available,
 * normalising every shape to a 0..1 scale where higher = better.
 *
 * Priority chain:
 *   1. ``rerank_score`` (when present, Jina v3 raw)
 *   2. invert ``final_score`` (lower = better → 1 - x)
 *   3. ``relevance_score`` (web specialist)
 *   4. fallback 0
 *
 * All inputs are clamped to [0..1]. Tested in
 * ``__tests__/computeRelevance.test.ts``.
 */
import type { AndoSource } from "@ando/core/domain";

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

export function computeRelevance(source: AndoSource): number {
  if (typeof source.rerank_score === "number") {
    return clamp01(source.rerank_score);
  }
  if (typeof source.final_score === "number") {
    return clamp01(1 - source.final_score);
  }
  return clamp01(source.relevance_score ?? 0);
}
